import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '@app/env-service';

interface AgGridData {
  rows :any[];
  lastRow :number;
}
@Injectable({
  providedIn: 'root',
})
export class SalesService {
  url :string = environment.url;
  urlV2 :string = environment.urlV2;

  private readonly _orders = new BehaviorSubject<any>(<any>[]);
  private readonly _submittingForm = new BehaviorSubject<boolean>(<boolean>false);

  public orders$ = this._orders.asObservable();
  submittingForm$ = this._submittingForm.asObservable();

  public gettingRecords :boolean = false;

  constructor(private http :HttpClient) {}

  getPendingTransactions(params :any) {
    return this.http.get(
      `${this.url}/sales/shipstation_sales/pending_transactions`,
      {
        params: params,
      },
    );
  }

  reprocessOrdersWithPurgatorySettings() {
    return this.http.post(
      `${this.url}/sales/shipstation_sales/rerun_purgatory_settings`,
      {},
    );
  }

  runAutoMapping(applyPurgatorySetting :boolean) {
    const payload :any = {};
    if (applyPurgatorySetting) {
      payload.applyPurgatorySetting = true;
    }

    return this.http.post(
      `${this.url}/sales/shipstation_sales/order_auto_mapping`,
      payload,
    );
  }

  getSync(params) {
    return this.http.get(`${this.url}/sales/shipstation_sales`, {
      params: params,
    });
  }

  getCount(params) {
    const p :any = Object.assign({}, params);
    p.cnt = 1;

    return this.http.get(`${this.url}/sales/shipstation_sales`, { params: p });
  }

  // downloadREport(params){
  //     params.
  // }

  getAsync(params) :Observable<any> {
    return this.http.get(`${this.url}/sales/shipstation_sales`, {
      params: params,
    });
  }

  getSalesSync(payload) {
    return this.http.post(`${this.url}/sales/shipstation_sales/list`, payload);
  }

  getSalesOrders(payload :any, relations ?:string, customRelations ?:boolean) {
    return this.http.post(
      `${this.url}/sales/shipstation_sales/sales_orders?customRelations=${
        customRelations || false
      }&relations=${relations || ''}`,
      payload,
    );
  }

  updateSalesOrder(data :any, salesOrderId :number) :Observable<any> {
    return this.http.put(`${this.urlV2}/orders/sales/${salesOrderId}`, data).pipe(
      map((response :any) => {
        this._submittingForm.next(false);

        return response;
      }),
      catchError((response) => {
        this._submittingForm.next(false);

        return throwError(() => response.error);
      }),
    );
  }

  getMasterSkuSales(payload) {
    return this.http.post(
      `${this.url}/sales/shipstation_sales/master_sku_sales`,
      payload,
    );
  }

  get(params) {
    this.gettingRecords = true;

    return this.http
      .get(`${this.url}/sales/shipstation_sales`, { params: params })
      .subscribe((resp :any) => {
        this.gettingRecords = false;
        this._orders.next(resp);
      });
  }

  /**
   * Confirm and post sales order entry
   * @param any payload
   * @returns Observable
   */
  confirmAndPostSalesOrder(payload :any) :Observable<any> {
    return this.http.post(`${this.url}/sales/shipstation_sales/confirmed`, payload);
  }

  confirmShipstationOrder(orderId :number, payload :any) {
    return this.http.post(
      `${this.url}/sales/shipstation_sales/${orderId}`,
      payload,
    );
  }

  bulkConfirmOrders(payload :any) {
    return this.http.post(
      `${this.url}/sales/shipstation_sales/confirm_bulk`,
      payload,
    );
  }

  updateShipstationOrder(orderId :number, payload :any) {
    return this.http.post(
      `${this.url}/sales/shipstation_sales/${orderId}/update`,
      payload,
    );
  }

  deleteOrder(orderId :number, rollbackInv ?:boolean) {
    const params :any = {};
    if (rollbackInv) {
      params.rollbackInv = 1;
    }

    return this.http.delete(
      `${this.url}/sales/shipstation_sales/${orderId}/delete`,
      { params: params },
    );
  }

  bulkDeleteOrders(payload :any) {
    return this.http.post(
      `${this.url}/sales/shipstation_sales/bulk-delete`,
      payload,
    );
  }

  bulkUpdateOrderStatus(payload :any) {
    return this.http.post(
      `${this.url}/sales/shipstation_sales/update-order-status`,
      payload,
    );
  }

  shipOrderToShipstation(orderId :number) {
    return this.http.post(
      `${this.url}/sales/shipstation_sales/${orderId}/shipping`,
      {},
    );
  }

  getOrderUpdates(orderId :number) {
    return this.http.get(
      `${this.url}/sales/shipstation_sales/${orderId}/shipping`,
      {},
    );
  }

  getfulfillmentStatus(params ?:{
    [param :string] :string | string[];
  }) :Observable<any> {
    return this.http.get(
      `${this.url}/sales/shipstation_sales/fulfillment_summary`,
      { params: params },
    );
  }

  resyncFulfillmentStatus() :Observable<any> {
    return this.http.get(
      `${this.url}/sales/shipstation_sales/resync_fulfillment_summary`,
      {},
    );
  }

  resyncInventoryOrders() :Observable<any> {
    return this.http.post(`${this.urlV2}/integration/orders/sync`, {});
  }

  holdOrderUntil(payload :any) {
    return this.http.post(`${this.urlV2}/sales/sales-orders/holduntil`, payload);
  }

  saveStarredDetails(
    salesOrderId :number,
    starred :boolean = false,
    description :string = '',
  ) :Observable<any> {
    return this.http.put(
      `${this.urlV2}/sales/sales-orders/${salesOrderId}/starred`,
      {
        starred,
        description,
      },
    );
  }

  bulkUpdateTags(data :any) :Observable<any> {
    return this.http.post(`${this.urlV2}/orders/sales/bulk-update-tags`, data).pipe(
      map((response :any) => {
        this._submittingForm.next(false);

        return response;
      }),
      catchError((response) => {
        this._submittingForm.next(false);

        return throwError(() => response.error);
      }),
    );
  }

  flagSalesOrder(data :{
    orderId :number;
    params ?:{ [param :string] :string | string[] };
  }) :Observable<any> {
    const { orderId, params } = data;

    return this.http
      .get(`${this.urlV2}/orders/sales/flag/${orderId}`, { params })
      .pipe(
        map((response :any) => {
          return response;
        }),
        catchError((response) => {
          return throwError(() => response.error);
        }),
      );
  }

  unflagSalesOrder(data :{ orderId :number }) :Observable<any> {
    const { orderId } = data;

    return this.http.get(`${this.urlV2}/orders/sales/${orderId}/unflag`);
  }

  getSalesOrderStockLogs(salesOrderId :number) :Observable<any> {
    return this.http.get(`${this.urlV2}/orders/${salesOrderId}/stock-logs`, {});
  }

  generateEDI810FromSalesOrder(salesOrderId :number) :Observable<any> {
    return this.http.get(`${this.urlV2}/orders/${salesOrderId}/edi/810`);
  }
}
